<template>
  <div class="container-fluid">
    <ValidationObserver ref="formMessageRef">
      <form novalidate @submit.prevent="saveChanges">
        <page-header :page="page">
          <template #custom_actions>
            <ActionButton
              text="Regresar"
              class="mr-3"
              variant="outline-success"
              i="fas fa-arrow-circle-left"
              @click="$router.go(-1)"
            />
            <ActionButton
              type="submit"
              text="Guardar"
              variant="success"
              i="fas fa-save"
            />
          </template>
        </page-header>
        <div class="mt-n5">
          <SectionTitle
            icon="fas fa-file-alt"
            :content="pageTitle"
            container-class="tag-badge-main-info"
          />
          <SectionTitle has-line content="Información de mensaje" />
        </div>
        <div class="mx-3">
          <div class="row">
            <div class="col-md-2">
              <ValidationProvider v-slot="{ errors }">
                <custom-date-picker
                  v-model="info.startDate"
                  label="Fecha de Comienzo:"
                  placeholder="DD/MM/YYYY"
                  :error="errors[0]"
                  custom-error-msg=""
                  :max-date="info.expireDate ?? null"
                />
              </ValidationProvider>
            </div>
            <div class="col-md-2">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <custom-date-picker
                  v-model="info.expireDate"
                  label="Fecha Expiración:"
                  placeholder="DD/MM/YYYY"
                  :error="errors[0]"
                  custom-error-msg="Favor de seleccionar la fecha de expiración del mensaje"
                  :min-date="
                    info.startDate ? new Date(info.startDate) : new Date()
                  "
                />
              </ValidationProvider>
            </div>
            <div class="col-md-4">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <custom-drop-down
                  v-model="info.applicationId"
                  :options="applications"
                  label="Aplicación:"
                  fieldkey="id"
                  fieldname="name"
                  placeholder="Seleccione"
                  :error="errors[0]"
                  custom-error-msg="favor de seleccionar la aplicación del mensaje"
                  @input="onChangeApplicationHandler"
                />
              </ValidationProvider>
            </div>
            <div class="col-md-4">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <custom-drop-down
                  v-model="info.messageLocationCode"
                  :options="availableLocations"
                  label="Ubicación de mensaje:"
                  fieldkey="id"
                  fieldname="name"
                  placeholder="Seleccione"
                  :error="errors[0]"
                  custom-error-msg="Favor de seleccionar la ubicación del mensaje"
                />
              </ValidationProvider>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-md-8">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <custom-input
                  v-model="info.titleDescription"
                  maxlength="500"
                  label="Titular:"
                  :error="errors[0]"
                  custom-error-msg="Favor de entrar el título del mensaje"
                />
              </ValidationProvider>
            </div>
            <div class="col-md-4">
              <custom-file-input
                ref="documentRef"
                v-model="docValue"
                label="Documento:"
                label-position="left"
                :attachment-id="documentId"
                :accept="allowedFiles"
                required
                display-style="normal"
                @input="documentChanged"
              />
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-md-12">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <custom-input
                  v-model="info.messageShortDescription"
                  maxlength="500"
                  label="Mensaje Corto:"
                  :error="errors[0]"
                  custom-error-msg="Favor de entrar el resumen del mensaje"
                />
              </ValidationProvider>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-md-12">
              <custom-input
                v-model="info.link"
                maxlength="500"
                label="Enlace:"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-md-12 mt-3">
              <label class="input-label" for="">Mensaje:</label>
              <ValidationProvider v-slot="{ errors }" rules="required">
                <vue-editor v-model="info.messageDescription" />
                <error
                  v-if="errors[0]"
                  message="Favor de entrar el contenido del mensaje"
                  variant="no-border"
                />
              </ValidationProvider>
            </div>
          </div>
        </div>
        <div class="mt-5">
          <SectionTitle
            has-line
            content="Seleccione Rol(es) para ver mensaje"
          />
          <div class="row">
            <div class="col-md-12">
              <user-role-selection
                :role-list="roles"
                :selected-roles="info.roles"
                :show-error="rolSelectorErrorIndicator"
                :show-user-selection="false"
                @on-select-rol="onSelect"
                @on-unselect-role="onUnSelectRole"
                @on-unselect-all="onUnSelectAll"
                @on-change-rol="onChangeRol"
              />
            </div>
          </div>
        </div>
        <div class="mt-5">
          <SectionTitle
            has-line
            content="Seleccione Usuario(s) para ver mensaje"
          />
          <div class="row">
            <div class="col-md-12">
              <user-role-selection
                :role-list="roles"
                :user-list="userList"
                :selected-users="info.userList"
                :show-error="rolSelectorErrorIndicator"
                @on-select-rol="onSelect"
                @on-select-user="onSelectUser"
                @on-unselect-user="onUnSelectUser"
                @on-unselect-role="onUnSelectRole"
                @on-unselect-all="onUnSelectAll"
                @on-change-rol="onChangeRol"
              />
            </div>
          </div>
        </div>
      </form>
    </ValidationObserver>
  </div>
</template>

<script>
import { VueEditor } from "vue2-editor";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import PageHeader from "@/components/common/page/PageHeader";
import ActionButton from "@/components/common/ActionButton";
import CustomDropDown from "@/components/common/CustomDropDown";
import CustomDatePicker from "@/components/common/CustomDatePicker";
import CustomInput from "@/components/common/CustomInput";
import { SectionTitle } from "@/components/pei/shared";
import CustomFileInput from "@/components/common/CustomFileInput";
import Error from "@/components/common/Error";
import UserRoleSelection from "@/components/common/UserRoleSelection";
import alertMixin from "@/mixin/alert.mixin";
import { modulesName } from "@/modules";
import objectToFormData from "object-to-formdata";
import { createNamespacedHelpers } from "vuex";
import {
  getterTypes,
  actionTypes
} from "@/modules/operation-message/store/types";
import service from "@/modules/operation-calendar-event/api";
const { mapGetters, mapActions } = createNamespacedHelpers(
  modulesName.operationMessageModuleName
);

const messageCapableApplications = Object.freeze([
  "e2ab3f04-b958-4755-8a1b-da0b75920997", //Mipe
  "153125d6-dec8-420f-9b14-2238fcb4b1bc", //Finanzas
  "964eb4c0-fb90-4f2e-a79f-b726d4e20077", //RemPro
  "fb7fcf39-0ea8-4106-9157-d9be1bb99544", //Parent
  "6944a8f7-7f89-4ceb-ad45-537bdc0a4b1e" //TechnologyAssistance
]);

import applications from "@/utils/constants/applications.js";
import messageLocationCode from "@/utils/constants/messageLocationCode.js";

export default {
  name: "MessageManagement",
  mixins: [alertMixin],
  components: {
    VueEditor,
    ValidationObserver,
    ValidationProvider,
    PageHeader,
    ActionButton,
    SectionTitle,
    CustomDropDown,
    CustomDatePicker,
    CustomInput,
    CustomFileInput,
    UserRoleSelection,
    Error
  },
  data: () => ({
    isEditMode: false,
    messageIdToEdit: null,
    page: {
      icon: "fas fa-comments-alt",
      title: "Mensajes",
      routes: ["Inicio", "Mensajes"]
    },
    info: {
      roles: [],
      userList: []
    },
    allowedFiles: ".pdf",
    documentId: null,
    docValue: null,
    rolSelectorErrorIndicator: false,
    userList: [],
    isFileDeleted: false
  }),
  computed: {
    ...mapGetters({
      locations: getterTypes.GET_LOCATION,
      roles: getterTypes.GET_ROL,
      currentFilters: getterTypes.GET_CURRENT_FILTER
    }),
    applications() {
      return this.getApplication().filter(a =>
        messageCapableApplications.includes(a.id)
      );
    },
    pageTitle() {
      return !this.isEditMode
        ? "Crear nuevo Mensaje de Mantenimiento"
        : "Mensaje de Mantenimiento";
    },
    hasDocument() {
      return this.documentId !== null && this.documentId.length !== 0;
    },
    availableLocations() {
      if (
        this.info.applicationId?.id?.toLowerCase() ===
        applications.Parent.toLowerCase()
      ) {
        return this.locations.filter(x =>
          [
            messageLocationCode.Inicio,
            messageLocationCode.InicioConAcuse
          ].includes(x.id)
        );
      } else {
        return this.locations;
      }
    }
  },
  mounted() {
    const { isEditMode, id } = this.$route.params;
    if (isEditMode) {
      this.isEditMode = isEditMode;
      this.messageIdToEdit = id;

      this.loadMessage(id);
    }
    this[actionTypes.FETCH_ROL]();
    this[actionTypes.FETCH_LOCATION]();
    this[actionTypes.FETCH_APPLICATION]();
  },
  methods: {
    ...mapActions([
      actionTypes.FETCH_MESSAGE_BY_ID,
      actionTypes.SAVE_CHANGES,
      actionTypes.FETCH_RESULT,
      actionTypes.FETCH_APPLICATION,
      actionTypes.FETCH_LOCATION,
      actionTypes.FETCH_ROL
    ]),
    ...mapGetters([getterTypes.GET_APPLICATION]),
    async saveChanges() {
      const isValid = await this.$refs.formMessageRef.validate();
      const viewErrorIndicator = this.verifyAddedRoles();
      if (!isValid || viewErrorIndicator) {
        this.ShowToast(
          "Alerta",
          "Favor de completar la información solicitada para crear mensaje.",
          "error"
        );
      } else {
        const paramsToSend = {
          ...this.info,
          users: this.info.userList,
          applicationId: this.info.applicationId?.id,
          messageLocationCode: this.info.messageLocationCode?.id,
          files: this.docValue,
          isFileDeleted: this.isFileDeleted
        };

        this.isFileDeleted = false;

        let formData = objectToFormData(paramsToSend, { indices: true });
        const saveResult = await this[actionTypes.SAVE_CHANGES]({
          isEditMode: this.isEditMode || false,
          formData
        });
        if (saveResult) {
          this.ShowToast(
            "Proceso Exitoso.",
            "El mensaje ha sido guardado exitosamente.",
            "success"
          );
          this.refreshSearch();
          this.$router.go(-1);
        }
      }
    },
    verifyAddedRoles() {
      let viewErrorIndicator = false;
      this.rolSelectorErrorIndicator = viewErrorIndicator =
        this.info.roles.length == 0 && this.info.userList.length == 0;
      return viewErrorIndicator;
    },
    async refreshSearch() {
      this[actionTypes.FETCH_RESULT]({ filters: this.currentFilters });
    },
    async loadMessage(id) {
      const message = await this[actionTypes.FETCH_MESSAGE_BY_ID]({ id });
      if (message) {
        this.info = {
          ...message,
          userList: message.users,
          expireDate: new Date(message.expireDate),
          applicationId: this.applications.find(
            x => x.id == message.applicationId
          ),
          messageLocationCode: this.locations.find(
            x => x.id == message.messageLocationCode
          )
        };
        this.documentId = this.info.documentId;
      }
    },
    documentChanged(documentElement) {
      if (documentElement === null) {
        this.documentId = null;
        this.docValue = null;
        this.isFileDeleted = true;
      }
    },
    onSelect(isAll, item) {
      if (!isAll && item) {
        this.info.roles.push(item);
      }
      if (isAll) {
        item.forEach(i => {
          this.info.roles.push(i);
        });
      }
    },
    onSelectUser(isAll, item) {
      if (!isAll && item) {
        this.info.userList.push(item);
      }
      if (isAll) {
        item.forEach(i => {
          this.info.userList.push(i);
        });
      }
    },
    onUnSelectRole(item) {
      if (item && item.id) {
        this.info.roles = this.info.roles.filter(x => x.id !== item.id);
      }
    },
    onUnSelectUser(item) {
      if (item && item.id) {
        this.info.userList = this.info.userList.filter(x => x.id !== item.id);
      }
    },
    onUnSelectAll(inUserSlectionMode) {
      if (inUserSlectionMode) {
        this.info.userList = [];
      } else {
        this.info.roles = [];
      }
    },
    async onChangeRol(item) {
      if (!item) {
        this.userList = [];
        return;
      }
      await service
        .fetchUsers(item.id)
        .then(res => {
          this.userList = res.data;
        })
        .catch(error => {
          this.ShowToast("", error.data.errors.errors[0], "error");
        });
    },
    onChangeApplicationHandler(value) {
      const availableLocationCodes = [
        messageLocationCode.Inicio,
        messageLocationCode.InicioConAcuse
      ];
      const selectedLocationCodeId = this.info.messageLocationCode?.id;

      if (
        value?.id?.toLowerCase() === applications.Parent.toLowerCase() &&
        !availableLocationCodes.includes(selectedLocationCodeId)
      ) {
        this.info.messageLocationCode = null;
      }
    }
  }
};
</script>

<style scoped lang="scss">
::v-deep .tag-badge {
  .orange-icon {
    font-size: 18pt;
    margin-left: 0px;
    line-height: 26px;
  }
}
</style>
